import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiCopy, FiEye, FiEyeOff, FiArrowUpCircle, FiCreditCard, FiDollarSign, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import { apiRequest, getTransferJobStatus } from '../utils/api';
import { useI18n } from '../i18n/useI18n';

export function WithdrawModal({ open, onClose, wallet, whitelist = [], pushToast, onSuccess }) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [toAddress, setToAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [token, setToken] = useState('USDC');
  const slippageBps = 30;
  const slippageFactor = 1 - slippageBps / 10000;

  // 检查地址是否在白名单中
  const isAddressValid = useMemo(() => {
    if (!toAddress || toAddress.length !== 42 || !toAddress.startsWith('0x')) {
      return true; // 格式不正确时不显示白名单错误
    }
    const normalizedAddress = toAddress.toLowerCase();
    const normalizedWhitelist = whitelist.map(addr => addr.toLowerCase());
    return normalizedWhitelist.includes(normalizedAddress);
  }, [toAddress, whitelist]);

  // 弹窗打开时锁定背景滚动
  useEffect(() => {
    if (!open) return;

    const scrollY = window.scrollY || 0;
    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyPosition = document.body.style.position;
    const prevBodyTop = document.body.style.top;
    const prevBodyWidth = document.body.style.width;
    const prevHtmlOverflow = document.documentElement.style.overflow;

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.position = prevBodyPosition;
      document.body.style.top = prevBodyTop;
      document.body.style.width = prevBodyWidth;
      window.scrollTo(0, scrollY);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setToAddress('');
      setAmount('');
      setToken('USDC');
    }
  }, [open]);

  if (!open || !wallet) return null;

  const isSafe = wallet.wallet_type?.toLowerCase() === 'safe';
  const isPrivy = wallet.wallet_provider === 'privy';
  const availableTokens = (isSafe || isPrivy) ? ['USDC'] : ['USDC', 'POL'];

  const usdcNative = wallet.usdc_native || 0;
  const usdcBridged = wallet.usdc_balance || 0;
  const bridgedConvertible = usdcBridged >= 0.5 ? usdcBridged * slippageFactor : 0;
  const totalUsdc = usdcNative + bridgedConvertible;
  const maxWithdrawUsdc = Math.max(0, Math.floor(totalUsdc * 1_000_000) / 1_000_000);

  const pollJobStatus = async (jobId) => {
    const maxAttempts = 80;
    for (let i = 0; i < maxAttempts; i++) {
      try {
        const statusRes = await getTransferJobStatus(wallet.user_id, jobId);
        const data = statusRes.data || {};
        if (data.status === 'success') {
          pushToast(t('withdraw.success'), 'success');
          setLoading(false);
          onClose();
          onSuccess?.();
          return;
        }
        if (data.status === 'processing' && data.tx_hash) {
          pushToast('转账已提交，等待链上确认', 'info');
          setLoading(false);
          onClose();
          onSuccess?.();
          return;
        }
        if (data.status === 'failed') {
          pushToast(data.error_msg || t('withdraw.failed'), 'error');
          setLoading(false);
          return;
        }
      } catch (err) {
        // 忽略单次查询错误，继续重试
      }
      await new Promise((r) => setTimeout(r, 3000));
    }
    pushToast(t('withdraw.timeout'), 'warning');
    setLoading(false);
  };

  const handleWithdraw = async () => {
    if (!toAddress || !amount) {
      pushToast(t('withdraw.fillAll'), 'warning');
      return;
    }
    if (!toAddress.startsWith('0x') || toAddress.length !== 42) {
      pushToast(t('withdraw.invalidTo'), 'warning');
      return;
    }
    if (parseFloat(amount) <= 0) {
      pushToast(t('withdraw.amountGt0'), 'warning');
      return;
    }

    try {
      setLoading(true);
      const formData = new URLSearchParams();
      formData.append('to_address', toAddress);
      formData.append('amount', amount);
      formData.append('token', token);
      formData.append('async_mode', 'true');

      const res = await apiRequest(`/api/v1/user/${wallet.user_id}/wallets/${wallet.wallet_address}/transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      if (res.code === 0) {
        pushToast(t('withdraw.submitOk'), 'info');
        const jobId = res.data?.job_id;
        if (jobId) {
          pollJobStatus(jobId);
        } else {
          setLoading(false);
        }
      } else {
        pushToast(res.msg || t('withdraw.failed'), 'error');
        setLoading(false);
      }
    } catch (e) {
      const msg = e?.message || t('wallets.requestFailed');
      pushToast(msg, 'error');
      setLoading(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[210] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-slate-700/60 bg-gradient-to-b from-slate-900/95 to-slate-950/95 backdrop-blur-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-rose-500/15 rounded-xl">
              <FiArrowUpCircle className="w-5 h-5 text-rose-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">{t('withdraw.title')}</h3>
              <p className="text-xs text-slate-500 uppercase tracking-wider">
                {wallet.wallet_address.slice(0, 6)}...{wallet.wallet_address.slice(-4)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* From Wallet Info */}
          <div className="p-4 bg-slate-800/40 rounded-xl border border-slate-700/50">
            <div className="flex items-center gap-2 mb-2">
              <FiCreditCard className="w-4 h-4 text-slate-500" />
              <span className="text-xs text-slate-500 uppercase tracking-wider">{t('withdraw.fromWallet')}</span>
            </div>
            <code className="text-sm font-mono text-slate-300 break-all">
              {wallet.wallet_address}
            </code>
          </div>

          {/* Token Selection */}
          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider ml-1">{t('withdraw.token')}</label>
            <div className="flex gap-2">
              {availableTokens.map((tokenSymbol) => (
                <button
                  key={tokenSymbol}
                  onClick={() => setToken(tokenSymbol)}
                  className={`
                    flex-1 py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2
                    ${token === tokenSymbol 
                      ? 'bg-sky-500 text-white shadow-lg shadow-sky-500/20' 
                      : 'bg-slate-800/60 text-slate-400 hover:bg-slate-700 hover:text-slate-200 border border-slate-700/50'}
                  `}
                >
                  <FiDollarSign className="w-4 h-4" />
                  {tokenSymbol}
                </button>
              ))}
            </div>
          </div>

          {/* Amount Input */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider ml-1">{t('withdraw.amount')}</label>
              <span className="text-xs text-slate-500">
                {t('withdraw.balance')}: <span className="text-slate-300 font-medium">{token === 'USDC' ? maxWithdrawUsdc.toFixed(6) : wallet.pol_balance} {token}</span>
              </span>
            </div>
            <div className="relative">
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full bg-slate-950/60 border border-slate-700/60 rounded-xl pl-4 pr-20 py-3.5 text-white placeholder-slate-600 focus:outline-none focus:border-sky-500/50 focus:ring-2 focus:ring-sky-500/20 transition-all text-lg"
              />
              <button 
                onClick={() => setAmount(token === 'USDC' ? maxWithdrawUsdc : wallet.pol_balance)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-sky-400 hover:text-sky-300 font-bold px-3 py-1.5 rounded-lg hover:bg-sky-500/10 transition-colors border border-sky-500/30"
              >
                {t('withdraw.max')}
              </button>
            </div>
          </div>

          {/* To Address Input */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider ml-1">{t('withdraw.toAddress')}</label>
              {/* 白名单状态提示 */}
              {toAddress && toAddress.length === 42 && toAddress.startsWith('0x') && (
                <div className="flex items-center gap-1.5">
                  {isAddressValid ? (
                    <span className="text-[11px] text-emerald-400 flex items-center gap-1">
                      <FiCheckCircle className="w-3 h-3" />
                      地址已验证
                    </span>
                  ) : (
                    <span className="text-[11px] text-rose-400 flex items-center gap-1">
                      <FiAlertCircle className="w-3 h-3" />
                      地址未在白名单中
                    </span>
                  )}
                </div>
              )}
            </div>
            <input
              type="text"
              value={toAddress}
              onChange={(e) => setToAddress(e.target.value)}
              placeholder="0x..."
              className={`w-full bg-slate-950/60 border rounded-xl px-4 py-3.5 text-white placeholder-slate-600 focus:outline-none focus:ring-2 transition-all font-mono text-sm ${
                toAddress && toAddress.length === 42 && toAddress.startsWith('0x') && !isAddressValid
                  ? 'border-rose-500/50 focus:border-rose-500 focus:ring-rose-500/20'
                  : 'border-slate-700/60 focus:border-sky-500/50 focus:ring-sky-500/20'
              }`}
            />
            {/* 白名单错误提示 */}
            {toAddress && toAddress.length === 42 && toAddress.startsWith('0x') && !isAddressValid && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30">
                <FiAlertCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                <div className="text-[11px] text-rose-300 leading-relaxed">
                  <span className="font-medium">该收款地址不在白名单中</span>
                  <br />
                  无法完成转账，请先点击右上角「管理白名单」添加该地址。
                </div>
              </div>
            )}
            {/* 正常提示 */}
            {!(toAddress && toAddress.length === 42 && toAddress.startsWith('0x') && !isAddressValid) && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
                <FiAlertCircle className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  {(isSafe || isPrivy) ? t('withdraw.safeHint') : t('withdraw.eoaHint')}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800/50 bg-slate-900/30 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors text-sm font-medium border border-slate-700/50"
          >
            {t('withdraw.cancel')}
          </button>
          <button
            onClick={handleWithdraw}
            disabled={loading || (toAddress && toAddress.length === 42 && toAddress.startsWith('0x') && !isAddressValid)}
            className="px-6 py-2.5 rounded-xl bg-rose-500/15 hover:bg-rose-500/25 text-rose-400 hover:text-rose-300 text-sm font-bold transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 border border-rose-500/30"
          >
            {loading ? <div className="w-4 h-4 border-2 border-rose-400/30 border-t-rose-400 rounded-full animate-spin" /> : <FiArrowUpCircle className="w-4 h-4" />}
            {t('withdraw.confirm')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
